import React, { useState, useEffect } from 'react';
import { X, Twitter, Brain, Share2, CheckCircle, AlertCircle } from 'lucide-react';
import twitterService from '../services/twitterService';

const EnhancedCallModal = ({ 
  isOpen, 
  onClose, 
  token, 
  onConfirmCall, 
  onNavigateToPremium 
}) => {
  const [thesis, setThesis] = useState('');
  const [thesisLoading, setThesisLoading] = useState(false);
  const [thesisError, setThesisError] = useState(null);
  const [twitterEnabled, setTwitterEnabled] = useState(true); // Always enabled
  const [twitterLoading, setTwitterLoading] = useState(false);
  const [selectedTone, setSelectedTone] = useState('bullish');
  const [callLoading, setCallLoading] = useState(false);
  const [lastGeneratedKey, setLastGeneratedKey] = useState(null);

  const tones = [
    { value: 'bullish', label: 'Bullish', description: 'Confident and enthusiastic' },
    { value: 'cautious', label: 'Cautious', description: 'Measured but optimistic' },
    { value: 'technical', label: 'Technical', description: 'Data-driven analysis' },
    { value: 'narrative', label: 'Narrative', description: 'Community-focused' }
  ];

  useEffect(() => {
    if (isOpen && token) {
      // Create stable key based on contract address and tone to prevent unnecessary regeneration
      const generateKey = `${token.contractAddress}-${selectedTone}`;
      
      // Only generate if key changed (prevents re-generation on token prop updates)
      if (generateKey !== lastGeneratedKey) {
        loadTwitterStatus();
        generateThesis();
        setLastGeneratedKey(generateKey);
      }
    }
  }, [isOpen, token, selectedTone, lastGeneratedKey]);

  // Regenerate thesis when tone changes - merged into main useEffect above
  // useEffect(() => {
  //   if (isOpen && token) {
  //     generateThesis();
  //   }
  // }, [selectedTone]);

  const loadTwitterStatus = async () => {
    try {
      // Always enable Twitter posting - no toggle needed
      setTwitterEnabled(true);
    } catch (error) {
      console.error('Failed to load Twitter status:', error);
      setTwitterEnabled(true); // Still enable even if there's an error
    }
  };

  const generateThesis = async (forceRegenerate = false) => {
    if (!token) return;
    
    setThesisLoading(true);
    setThesisError(null);
    
    try {
      const sessionId = localStorage.getItem('sessionId');
      const tokenData = {
        symbol: token.symbol,
        name: token.name,
        contractAddress: token.contractAddress,
        marketCap: token.marketCap || 0,
        price: token.price || 0,
        jupiterData: token.jupiterData || {}
      };
      
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://api.degen-oracle.com'}/api/user/generate-thesis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId,
          tokenData,
          tone: selectedTone,
          forceRegenerate
        })
      });
      
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate thesis: ${response.status}`);
      }
      
      const result = await response.json();
      setThesis(result.thesis);
    } catch (error) {
      setThesisError('Failed to generate thesis: ' + error.message);
      console.error('Thesis generation error:', error);
    } finally {
      setThesisLoading(false);
    }
  };

  const handleTwitterToggle = async (enabled) => {
    setTwitterLoading(true);
    try {
      await twitterService.setTwitterPostingEnabled(enabled);
      setTwitterEnabled(enabled);
    } catch (error) {
      console.error('Failed to update Twitter preference:', error);
      alert('Failed to update Twitter posting preference');
    } finally {
      setTwitterLoading(false);
    }
  };

  const handleConfirmCall = async () => {
    console.log('🚀 EnhancedCallModal: handleConfirmCall called', {
      token: token?.symbol,
      thesis: thesis?.substring(0, 50) + '...',
      twitterEnabled,
      tone: selectedTone
    });
    
    setCallLoading(true);
    try {
      await onConfirmCall({
        token,
        thesis,
        twitterEnabled,
        tone: selectedTone
      });
      console.log('✅ EnhancedCallModal: Call confirmed successfully');
      onClose();
    } catch (error) {
      console.error('❌ EnhancedCallModal: Call confirmation error:', error);
      alert('Failed to make call: ' + (error.message || 'Unknown error'));
    } finally {
      setCallLoading(false);
    }
  };

  if (!isOpen || !token) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      
      <div className="relative bg-dark-card border border-gray-700 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">
                {token.symbol?.slice(0, 3)}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">
                Make Your Call
              </h2>
              <p className="text-gray-400 text-sm">
                {token.name} • ${(token.marketCap / 1000000).toFixed(1)}M MC
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Thesis Generation */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium text-white flex items-center gap-2">
              <Brain size={18} className="text-purple-400" />
              AI-Generated Thesis
            </h3>
            <div className="flex gap-2">
              {tones.map(tone => (
                <button
                  key={tone.value}
                  onClick={() => setSelectedTone(tone.value)}
                  className={`px-3 py-1 text-xs rounded-lg border transition-colors ${
                    selectedTone === tone.value
                      ? 'bg-purple-600 border-purple-500 text-white'
                      : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {tone.label}
                </button>
              ))}
            </div>
          </div>

          {thesisLoading ? (
            <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="animate-spin w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full"></div>
                <span className="text-gray-300">Generating thesis...</span>
              </div>
            </div>
          ) : thesisError ? (
            <div className="bg-red-900/20 border border-red-600 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <AlertCircle size={20} className="text-red-400" />
                <span className="text-red-300">{thesisError}</span>
              </div>
            </div>
          ) : (
            <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
              <textarea
                value={thesis}
                onChange={(e) => setThesis(e.target.value)}
                className="w-full bg-transparent text-gray-200 leading-relaxed resize-none border-none outline-none min-h-[100px]"
                placeholder="AI-generated thesis will appear here, or write your own..."
                rows={4}
              />
            </div>
          )}

          <button
            onClick={() => generateThesis(true)}
            disabled={thesisLoading}
            className="mt-3 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {thesisLoading ? 'Generating...' : 'Regenerate Thesis'}
          </button>
        </div>

        {/* Twitter Posting Settings */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-white flex items-center gap-2 mb-3">
            <Twitter size={18} className="text-blue-400" />
            Twitter Posting
          </h3>
          
          <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <CheckCircle size={16} className="text-blue-400" />
              <div>
                <p className="text-gray-200 font-medium">Auto-post to Twitter</p>
                <p className="text-gray-400 text-sm">
                  All calls and milestone updates will be posted to Twitter automatically
                </p>
              </div>
            </div>
            
            <div className="mt-3 p-3 bg-blue-900/20 border border-blue-600/30 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-blue-400" />
                <span className="text-blue-300 text-sm">
                  Your call and all milestone updates will be posted to Twitter automatically
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Call Preview */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-white flex items-center gap-2 mb-3">
            <Share2 size={18} className="text-green-400" />
            Call Preview
          </h3>
          
          <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">Token:</span>
                <span className="text-white font-medium">${token.symbol}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">Market Cap:</span>
                <span className="text-white font-medium">${(token.marketCap / 1000000).toFixed(1)}M</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">Tone:</span>
                <span className="text-white font-medium capitalize">{selectedTone}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">Twitter:</span>
                <span className={`font-medium ${twitterEnabled ? 'text-green-400' : 'text-gray-400'}`}>
                  {twitterEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmCall}
            disabled={callLoading || thesisLoading || !thesis}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {callLoading ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                Making Call...
              </>
            ) : (
              <>
                <CheckCircle size={18} />
                Make Call
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedCallModal;
